import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Check, CircleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TeamRosterEntry, useAssistantApi } from '../../../common/hooks/use-assistant-api.hook';
import { useContentStudioApi } from '../../../common/hooks/use-content-studio-api.hook';
import { CsAgentRun } from '../../../common/models/content-studio.model';
import { useAskAi } from '../../../modules/vendor/assistant/AskAiContext';
import { TopNavBar } from '../../../common/Header/TopNavBar';
import { ArdiLogPanel, ArdiStatusPill, useArdiStatus } from '../../../modules/vendor/ardi/ArdiPulse';
import { ArdiActivityFeed } from '../../../modules/vendor/ardi/ArdiActivityFeed';
import { relativeTime } from '../../../modules/vendor/insights/insight-presentation';
import { agentName, agentOfRun, agentRole, plainLine, toolLabel } from '../../../modules/vendor/agents/agents';
import { AgentAvatar } from '../../../modules/vendor/agents/components/AgentAvatar';
import { TemplatesGallery } from '../../../modules/vendor/agents/components/TemplatesGallery';
import { Connection, useHomeApi } from '../../../common/hooks/use-home-api.hook';

/**
 * The team, and everything it has done.
 *
 * Left: who is on it — the lead, the specialists it sends work to, the watch
 * that runs every morning, and the long-running workers (analysis, studio,
 * catalogue). Each says what it can read and what it last did; one it cannot
 * staff for this vendor says what connecting would add. Right: the activity
 * feed, every run newest first, each with the steps it took — the record that
 * turns "AI" from a claim into work the vendor can inspect.
 */

const KIND_ORDER: Array<TeamRosterEntry['kind']> = ['lead', 'specialist', 'always-on', 'worker'];

const STATUS_DOT: Record<string, string> = {
    running: 'bg-success animate-pulse',
    pending: 'bg-success animate-pulse',
    paused: 'bg-warning',
    succeeded: 'bg-muted-foreground/40',
    failed: 'bg-destructive',
    cancelled: 'bg-muted-foreground/40',
};

const AgentsPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const askAi = useAskAi();
    const assistantApi = useAssistantApi();
    const studio = useContentStudioApi();
    const home = useHomeApi();
    const [connections, setConnections] = useState<Connection[] | null>(null);

    const [team, setTeam] = useState<TeamRosterEntry[] | null>(null);
    const [runs, setRuns] = useState<CsAgentRun[] | null>(null);
    const [selected, setSelected] = useState<string | null>(location.hash ? location.hash.slice(1) : null);
    const [openRun, setOpenRun] = useState<string | null>(null);

    const load = async () => {
        const [roster, list] = await Promise.all([
            assistantApi.getTeam().catch(() => [] as TeamRosterEntry[]),
            studio.listAgentRuns({ limit: 60 }).catch(() => [] as CsAgentRun[]),
        ]);
        setTeam(roster);
        setRuns(list);
    };

    useEffect(() => {
        void load();
        home.getConnections().then(setConnections).catch(() => setConnections([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const live = (runs ?? []).some((r) => r.status === 'pending' || r.status === 'running');
    useEffect(() => {
        if (!live) return;
        const id = window.setInterval(() => void load(), 5000);
        return () => window.clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [live]);

    const feed = useMemo(
        () => (runs ?? []).filter((r) => {
            const agent = agentOfRun(r);
            return agent && (!selected || agent === selected);
        }),
        [runs, selected],
    );

    const timeAgo = (iso?: string) => {
        if (!iso) return '';
        const r = relativeTime(iso);
        return t(r.key, { count: r.count });
    };

    const talkTo = (entry: TeamRosterEntry) => {
        const name = agentName(t, entry.id, entry.name);
        navigate('/home/chat');
        void askAi?.startChat(
            t('AGENTS.PAGE.TALK_TO_PROMPT', {
                name,
                defaultValue: 'Ask {{name}} what they would look at first for my business this week.',
            }) as string,
        );
    };

    const { status: ardiStatus } = useArdiStatus();
    const [ardiOpen, setArdiOpen] = useState(false);

    const grouped = KIND_ORDER.map((kind) => ({ kind, members: (team ?? []).filter((m) => m.kind === kind) }))
        .filter((g) => g.members.length);

    return (
        <div className="flex min-h-full flex-col">
        <TopNavBar breadcrumbs={[{ label: t('AGENTS.NAV.AGENTS', 'AI team') }]} />
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8 md:px-8">
            <div className="flex flex-col gap-1">
                <h1 className="text-2xl font-medium tracking-tight">{t('AGENTS.PAGE.TITLE', 'Your AI team')}</h1>
                <p className="text-sm text-muted-foreground">
                    {t('AGENTS.PAGE.SUBTITLE', 'Who works on your business, what each one can see, and everything they have done.')}
                </p>
            </div>

            {/* Every ready-made agent. Picking one takes its brief to Home's composer. */}
            <div className="-mx-4">
                <TemplatesGallery
                    connections={connections}
                    onPick={(_template, brief) => navigate('/home/dashboard', { state: { seed: brief } })}
                />
            </div>

            <div className="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
                {/* The roster. */}
                <div className="flex flex-col gap-5">
                    {!team && [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
                    {grouped.map((group) => (
                        <div key={group.kind} className="flex flex-col gap-2">
                            <p className="text-xs text-muted-foreground">{t(`AGENTS.PAGE.KIND.${group.kind.toUpperCase().replace('-', '_')}`)}</p>
                            {group.members.map((m) => {
                                const active = selected === m.id;
                                return (
                                    <div
                                        key={m.id}
                                        id={m.id}
                                        className={cn(
                                            'flex flex-col gap-2 rounded-xl border bg-card p-3.5 transition-colors',
                                            active ? 'border-foreground ring-1 ring-foreground' : 'hover:border-foreground/25',
                                        )}
                                    >
                                        <button type="button" onClick={() => setSelected(active ? null : m.id)} className="flex items-start gap-3 text-start">
                                            <AgentAvatar agent={m.id} size="lg" working={m.running > 0} />
                                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                                <span className="flex items-center gap-2">
                                                    <span className="truncate text-sm font-medium">{agentName(t, m.id, m.name)}</span>
                                                    {m.running > 0 && (
                                                        <span className="rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success">
                                                            {t('AGENTS.STATUS.WORKING', 'Working now')}
                                                        </span>
                                                    )}
                                                    {!m.available && (
                                                        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                                                            {t('AGENTS.STATUS.UNAVAILABLE', 'Needs a connection')}
                                                        </span>
                                                    )}
                                                </span>
                                                <span className="text-xs leading-relaxed text-muted-foreground">
                                                    {agentRole(t, m.id, m.role ?? '')}
                                                </span>
                                            </span>
                                        </button>

                                        {!!m.tools.length && (
                                            <div className="flex flex-wrap gap-1 ps-13">
                                                {m.tools.slice(0, 6).map((tool) => (
                                                    <span key={tool} className="rounded-md border px-1.5 py-0.5 text-[11px] text-muted-foreground">
                                                        {toolLabel(t, tool)}
                                                    </span>
                                                ))}
                                                {m.tools.length > 6 && (
                                                    <span className="px-1 py-0.5 text-[11px] text-muted-foreground">+{m.tools.length - 6}</span>
                                                )}
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between gap-3 ps-13">
                                            <span className="line-clamp-1 text-xs text-muted-foreground">
                                                {m.last
                                                    ? `${timeAgo(m.last.at)} · ${plainLine(m.last.headline ?? m.last.brief)}`
                                                    : t('AGENTS.STATUS.NEVER', 'Has not worked for you yet')}
                                            </span>
                                            {m.kind === 'specialist' && m.available && (
                                                <Button variant="ghost" size="sm" className="shrink-0 text-muted-foreground" onClick={() => talkTo(m)}>
                                                    {t('AGENTS.PAGE.TALK_TO', 'Put to work')}
                                                    <ArrowRight data-icon="inline-end" className="rtl:rotate-180" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* Everything they did. */}
                <div className="flex flex-col gap-2">
                    {/* What Ardi does between conversations: reacting to what comes in. */}
                    <div className="mb-4 flex flex-col gap-3 rounded-xl border bg-card p-4" data-testid="ardi-around-the-clock">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-medium">{t('ARDI.LOG.AROUND_THE_CLOCK')}</p>
                            <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setArdiOpen(true)}>
                                {t('ARDI.LOG.OPEN')}
                                <ArrowRight data-icon="inline-end" className="rtl:rotate-180" />
                            </Button>
                        </div>
                        <ArdiStatusPill status={ardiStatus} onOpen={() => setArdiOpen(true)} />
                        {ardiStatus && <ArdiActivityFeed items={ardiStatus.recent.slice(0, 4)} compact emptyText={t('ARDI.ACTIVITY.EMPTY_SHORT') as string} />}
                    </div>
                    <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">
                            {selected
                                ? t('AGENTS.PAGE.ACTIVITY_OF', { name: agentName(t, selected), defaultValue: 'What {{name}} did' })
                                : t('AGENTS.PAGE.ACTIVITY', 'Activity')}
                        </p>
                        {selected && (
                            <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setSelected(null)}>
                                {t('AGENTS.PAGE.SHOW_ALL', 'Show everyone')}
                            </Button>
                        )}
                    </div>

                    {!runs && [0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
                    {runs && !feed.length && (
                        <div className="rounded-xl border border-dashed px-6 py-10 text-center text-sm text-muted-foreground">
                            {t('AGENTS.PAGE.NO_ACTIVITY', 'Nothing yet. Ask something on Home and your team gets to work.')}
                        </div>
                    )}

                    <div className="flex flex-col divide-y rounded-xl border bg-card">
                        {feed.map((run) => {
                            const agent = agentOfRun(run)!;
                            const open = openRun === run.runId;
                            const headline = plainLine(run.result?.report?.headline ?? run.result?.summary ?? run.error);
                            const watch = run.profile === 'watch' ? run.result : null;
                            return (
                                <div key={run.runId} className="flex flex-col gap-2 px-4 py-3">
                                    <button type="button" onClick={() => setOpenRun(open ? null : run.runId)} className="flex items-start gap-3 text-start">
                                        <AgentAvatar agent={agent} size="sm" working={run.status === 'running' || run.status === 'pending'} className="mt-0.5" />
                                        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                            <span className="flex items-center gap-2 text-xs text-muted-foreground">
                                                <span className="font-medium text-foreground">{agentName(t, agent)}</span>
                                                <span className={cn('size-1.5 rounded-full', STATUS_DOT[run.status])} aria-hidden />
                                                <span>{timeAgo(run.finishedAt ?? run.createdAt)}</span>
                                                {!!run.steps?.length && <span>· {t('AGENTS.LIVE.STEPS', { count: run.steps.length, defaultValue: '{{count}} steps' })}</span>}
                                            </span>
                                            <span className="line-clamp-2 text-sm">
                                                {watch
                                                    ? t('AGENTS.PAGE.WATCH_RESULT', {
                                                        checks: watch.checks,
                                                        metrics: watch.metricsRead,
                                                        found: watch.found,
                                                        defaultValue: 'Ran {{checks}} checks over {{metrics}} numbers — {{found}} findings open.',
                                                    })
                                                    : run.message}
                                            </span>
                                            {headline && !watch && <span className="line-clamp-2 text-xs text-muted-foreground">{headline}</span>}
                                        </span>
                                    </button>

                                    {open && !!run.steps?.length && (
                                        <ul className="ms-9 flex flex-col gap-1 border-s ps-3">
                                            {run.steps.map((s) => (
                                                <li key={s.index} className="flex items-center gap-2 text-xs text-muted-foreground">
                                                    {s.status === 'failed'
                                                        ? <CircleAlert className="size-3.5 text-destructive" aria-hidden />
                                                        : <Check className="size-3.5" aria-hidden />}
                                                    {watch ? t(`AGENTS.DETECTOR.${s.tool.toUpperCase()}`, { defaultValue: toolLabel(t, s.tool) }) : toolLabel(t, s.tool)}
                                                    {s.status === 'proposed' && (
                                                        <span className="rounded-full bg-muted px-1.5 text-[10px]">{t('AGENTS.PAGE.PROPOSED', 'proposed')}</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {open && run.context?.assistantThreadUuid && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="ms-9 self-start text-muted-foreground"
                                            onClick={() => navigate(`/home/chat/${run.context!.assistantThreadUuid}`)}
                                        >
                                            {t('AGENTS.PAGE.OPEN_CONVERSATION', 'Open the conversation')}
                                            <ArrowRight data-icon="inline-end" className="rtl:rotate-180" />
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
        <ArdiLogPanel open={ardiOpen} onOpenChange={setArdiOpen} status={ardiStatus} />
        </div>
    );
};

export default AgentsPage;
